// React
import React, { Fragment, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Libraries
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCartShopping as fasCartShopping, faArrowRight as fasArrowRight } from '@fortawesome/pro-solid-svg-icons'

// Components
import BLauncher, { HostStyles } from './styles'
import MiniCart from 'shop-components/components/MiniCart'

// Shared
import registerCustomElement from 'shop-components/utils/registerCustomElement'
import withStore from 'shop-components/utils/withStore'
import withTheme from 'shop-components/utils/withTheme'
import { withTranslation } from 'shared/utils/withTranslation'
import Format from 'shared/utils/format'
import { setUserFrameworkCartData } from 'shared/utils/userFrameworkEvents'
import ClientActions from 'store/v2/redux/actions/client'

const Launcher = ({
  cart,
  cartOpen,
  toggleCart,
  useTimes,
  usePrices,
  timeFormat,
  itemCount,
  currency,
  totalPriceAttribute,
  t
}) => {
  useEffect(() => {
    setUserFrameworkCartData()
  }, [cart])

  return (
    <Fragment>
      <HostStyles />
      {cart && (
        <BLauncher.MiniCartContainer>
          <MiniCart inPortal t={t} />
        </BLauncher.MiniCartContainer>
      )}
      <BLauncher
        key={`booqable-launcher-${cartOpen}`}
        id="booqable-launcher"
        cartOpen={cartOpen}
        onClick={toggleCart.bind(null, !cartOpen)}
      >
        <BLauncher.Icon>
          <FontAwesomeIcon icon={fasCartShopping} size="lg" />
        </BLauncher.Icon>
        <BLauncher.Summary id="booqable-launcher-summary">
          <BLauncher.Details>
            {cart?.starts_at ?
              (
                <BLauncher.Dates>
                  <span>
                    {Format.date(cart.starts_at)}
                    {useTimes && Format.date(cart.starts_at, timeFormat)}
                  </span>
                  <FontAwesomeIcon icon={fasArrowRight} />
                  <span>
                    {Format.date(cart.stops_at)}
                    {useTimes && Format.date(cart.stops_at, timeFormat)}
                  </span>
                </BLauncher.Dates>
              ) :
              (
                <BLauncher.Dates>{t('user.store.select_a_period')}</BLauncher.Dates>
              )}
          </BLauncher.Details>
          <hr />
          <BLauncher.Details>
            {cart && itemCount ?
              (
                <div>
                  <span>
                    {itemCount} {t('common.resources.item', { count: itemCount })}
                  </span>
                  {usePrices && (
                    <span className="bq-total">{Format.money(cart?.get(totalPriceAttribute), currency)}</span>
                  )}
                </div>
              ) :
              (
                <span>{t('user.store.cart_empty')}</span>
              )}
          </BLauncher.Details>
        </BLauncher.Summary>
      </BLauncher>
    </Fragment>
  )
}

Launcher.displayName = 'MiniCartLauncher'

Launcher.propTypes = {
  cartOpen: PropTypes.bool,
  toggleCart: PropTypes.func,
  cart: PropTypes.object,
  useTimes: PropTypes.bool,
  usePrices: PropTypes.bool,
  timeFormat: PropTypes.string,
  totalPriceAttribute: PropTypes.string,
  currency: PropTypes.object,
  itemCount: PropTypes.number,
  t: PropTypes.func
}

const mapStateToProps = (state) => {
  let timeFormat, totalPriceAttribute

  if (state.settings.get('dates.use_am_pm')) {
    timeFormat = 'hh:mm A'
  } else {
    timeFormat = 'HH:mm'
  }

  const cart = state.orm.carts.getById(state.client.cartId)
  const lines = state.orm.lines.getAll({ id: cart?.lines, product_type: ['consumable', 'sales_item', 'rental', 'bundle'] })

  const itemCount = lines.map((line) => line.quantity).reduce((a, b) => a + b, 0)

  if (state.settings.get('pricing.tax_strategy') === 'inclusive') {
    totalPriceAttribute = 'grand_total_with_tax_in_cents'
  } else {
    totalPriceAttribute = 'grand_total_in_cents'
  }

  return {
    cartOpen: state.client.cartOpen,
    cart,
    currency: state.client.currency,
    totalPriceAttribute,
    timeFormat,
    useTimes: state.settings.get('store.use_times'),
    usePrices: state.settings.get('store.use_prices'),
    itemCount
  }
}

const mapDispatchToProps = () => {
  return {
    toggleCart: (cartOpen) => {
      return ClientActions.toggleCart(cartOpen)
    },
    closePicker: () => {
      return ClientActions.toggleModalPicker(false)
    }
  }
}

const decoratedComponent = compose(
  withTranslation('user'),
  withTheme,
  withStore,
  reduxConnect(mapStateToProps, mapDispatchToProps)
)(Launcher)

registerCustomElement('bq-minicart-launcher', decoratedComponent)

export default decoratedComponent
